import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogContent, MatDialogTitle } from '@angular/material/dialog';
import {
  MatTable,
  MatTableDataSource,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { map, startWith } from 'rxjs';
import {
  EmployeeRequestApprovalPolicyByRequestableType,
  EmployeeRequestApprovalPolicyEnum,
  EmployeeRequestApprovalPolicyEnumDescriptions,
} from 'src/app/enums/employee-request-approval-policy.enum';
import { EmployeeRequestResponsibleForAnswerStatusEnum } from 'src/app/enums/employee-request-responsible-for-answer-status.enum';
import {
  EmployeeRequestApprovalStatus,
  EmployeeRequestApprovalStatusDialogData,
  EmployeeRequestApprovalStatusResponsible,
  EmployeeRequestResponsibleForAnswer,
} from 'src/app/models/employee-request-responsible-for-answer.model';
import { EmployeeRequestApprovalPolicyPipe } from 'src/app/pipes/employee-request-approval-policy.pipe';
import { EmployeeRequestResponsibleForAnswerStatusPipe } from 'src/app/pipes/employee-request-responsible-for-answer-status.pipe';
import { TypeSafeMatCellDeferred } from 'src/app/directives/type-safe-mat-cell-def.directive';
import { ApiService } from 'src/app/services';

@Component({
  selector: 'app-employee-request-approval-status-dialog',
  templateUrl: './employee-request-approval-status-dialog.component.html',
  styleUrl: './employee-request-approval-status-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatDialogTitle,
    MatDialogContent,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatProgressSpinner,
    DatePipe,
    EmployeeRequestApprovalPolicyPipe,
    EmployeeRequestResponsibleForAnswerStatusPipe,
    TypeSafeMatCellDeferred,
  ],
})
export class EmployeeRequestApprovalStatusDialogComponent {
  private readonly api = inject(ApiService);
  readonly data = inject<EmployeeRequestApprovalStatusDialogData>(MAT_DIALOG_DATA);

  readonly EmployeeRequestApprovalPolicyEnum = EmployeeRequestApprovalPolicyEnum;

  readonly approvalStatusLoad = toSignal(
    this.api
      .getEmployeeRequestResponsibleForAnswers({
        requestable_type: this.data.requestable_type,
        requestable_id: this.data.requestable_id,
        with: 'responsible_user,company_product',
      })
      .pipe(
        map(({ data }): ApprovalStatusLoadResult => {
          const approvalPolicy = EmployeeRequestApprovalPolicyByRequestableType[this.data.requestable_type];

          return {
            loading: false,
            status: {
              approval_policy: approvalPolicy,
              policy_description: EmployeeRequestApprovalPolicyEnumDescriptions[approvalPolicy],
              responsibles: data.map(
                (answer: EmployeeRequestResponsibleForAnswer): EmployeeRequestApprovalStatusResponsibleRow => ({
                  responsible_user: answer.responsible_user,
                  company_product: answer.company_product,
                  status: answer.status,
                  responded_at: answer.responded_at,
                  should_show_status_as_dash: this.shouldShowStatusAsDash(this.data.requestable_status, answer.status),
                })
              ),
            },
          };
        }),
        startWith({ loading: true, status: null })
      ),
    { initialValue: { loading: true, status: null } satisfies ApprovalStatusLoadResult }
  );

  readonly loading = computed((): boolean => this.approvalStatusLoad().loading);

  readonly approvalStatus = computed((): EmployeeRequestApprovalStatusView | null => this.approvalStatusLoad().status);

  readonly responsiblesDataSource = computed((): MatTableDataSource<EmployeeRequestApprovalStatusResponsibleRow> => {
    const dataSource = new MatTableDataSource<EmployeeRequestApprovalStatusResponsibleRow>();
    dataSource.data = this.approvalStatus()?.responsibles ?? [];

    return dataSource;
  });

  readonly displayedColumns = computed((): string[] => {
    const columns = ['responsible'];

    if (this.approvalStatus()?.approval_policy === EmployeeRequestApprovalPolicyEnum.OnePerProduct) {
      columns.push('company_product');
    }

    columns.push('status', 'responded_at');

    return columns;
  });

  private shouldShowStatusAsDash(
    requestable_status: EmployeeRequestResponsibleForAnswerStatusEnum,
    responsible_status: EmployeeRequestResponsibleForAnswerStatusEnum
  ): boolean {
    return (
      requestable_status === EmployeeRequestResponsibleForAnswerStatusEnum.ACCEPTED &&
      responsible_status === EmployeeRequestResponsibleForAnswerStatusEnum.PENDING
    );
  }
}

interface ApprovalStatusLoadResult {
  loading: boolean;
  status: EmployeeRequestApprovalStatusView | null;
}

type EmployeeRequestApprovalStatusView = Omit<EmployeeRequestApprovalStatus, 'responsibles'> & {
  responsibles: EmployeeRequestApprovalStatusResponsibleRow[];
};

interface EmployeeRequestApprovalStatusResponsibleRow extends EmployeeRequestApprovalStatusResponsible {
  should_show_status_as_dash: boolean;
}
